import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

export const useVisitTracker = (itemId) => {
  const { user, token } = useAuth();
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isTracking, setIsTracking] = useState(false);
  const [userIP, setUserIP] = useState(null);
  const intervalRef = useRef(null);
  const initialTimeRef = useRef(null);

  // Obtener la IP del usuario
  const getUserIP = async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      console.error('Error obteniendo IP:', error);
      // Fallback: usar una IP local o generar un identificador único
      return `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
  };

  // Verificar si las cookies están aceptadas
  const areCookiesAccepted = () => {
    return localStorage.getItem('cookiesAccepted') === 'true';
  };

  // Detectar si estamos en una página de alquiler
  const isRental = () => {
    return window.location.pathname.includes('/alquiler/');
  };

  // ELIMINADO: Ya no necesitamos usar un ID falso
  // Ahora usamos el ID real del alquiler/propiedad

  // Inicializar IP cuando se aceptan las cookies
  useEffect(() => {
    const initializeIP = async () => {
      if (areCookiesAccepted() && !userIP) {
        const ip = await getUserIP();
        setUserIP(ip);
      }
    };

    initializeIP();

    // Escuchar cambios en localStorage para cookies
    const handleStorageChange = (e) => {
      if (e.key === 'cookiesAccepted' && e.newValue === 'true') {
        initializeIP();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [userIP]);

  useEffect(() => {
    // Verificar si tenemos la información necesaria para hacer tracking
    const canTrack = itemId && (
      (user && token) || // Usuario autenticado
      (areCookiesAccepted() && userIP) // Usuario anónimo con cookies aceptadas
    );

    if (!canTrack) return;

    // Configurar tiempo inicial
    initialTimeRef.current = Date.now();
    let isMounted = true;
    setIsTracking(true);

    const isRentalPage = isRental();
    
    // Función para generar un ID de visita
    const generateVisitId = (timestamp) => {
      const randomPart = Math.random().toString(36).substring(2, 8);
      const userIdentifier = user ? user.id : `ip_${userIP}`;
      return `${isRentalPage ? 'rental' : 'property'}-${itemId}-${userIdentifier}-${timestamp}-${randomPart}`;
    };

    // Función para registrar visita
    const trackVisit = async (currentTime) => {
      try {
        // Calcular tiempo transcurrido
        const calculatedElapsedTime = Math.floor((currentTime - initialTimeRef.current) / 1000);
        
        // Generar el ID de visita
        const visitId = generateVisitId(currentTime);
        
        // Usar el ID real del item (propiedad o alquiler)
        const trackingId = itemId;

        // El campo contact_clicked podría ser útil para marcar que es un alquiler
        const contactClicked = isRentalPage ? true : false;

        // Preparar datos para enviar
        const trackingData = {
          propertyId: trackingId, // Usar el ID real
          elapsedTime: calculatedElapsedTime,
          visitId: visitId,
          contactClicked: contactClicked,
          // Agregar información adicional para usuarios anónimos
          ...((!user || !token) && {
            isAnonymous: true,
            userIP: userIP,
            deviceId: localStorage.getItem('deviceId') || 'unknown'
          })
        };

        console.log(`Registrando visita para ${isRentalPage ? 'alquiler' : 'propiedad'} ID: ${itemId}`, {
          trackingId,
          visitId,
          contactClicked,
          isAnonymous: !user,
          userIP: userIP
        });

        // Configurar headers apropiados
        const headers = {
          'Content-Type': 'application/json'
        };

        // Solo agregar token si el usuario está autenticado
        if (user && token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        // Enviar solicitud a la API
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL || 'https://backend-production-1786.up.railway.app/api'}/track-visit`,
          trackingData,
          { headers }
        );

        if (response.data.success) {
          console.log('Visita registrada correctamente:', {
            propertyId: trackingId,
            elapsedTime: calculatedElapsedTime,
            visitId: visitId,
            contactClicked,
            isAnonymous: !user,
            averageTime: response.data.averageTime || null
          });
          
          // Si el servidor devuelve un tiempo promedio, podríamos usarlo para alguna métrica
          if (response.data.averageTime) {
            console.log(`Tiempo promedio de visita: ${response.data.averageTime} segundos`);
          }
        }
      } catch (error) {
        console.error('Error al registrar visita:', error.response?.data || error.message);
        
        // Si es un error de autenticación y tenemos cookies aceptadas, 
        // podríamos intentar como usuario anónimo
        if (error.response?.status === 401 && areCookiesAccepted() && userIP) {
          console.log('Reintentando como usuario anónimo...');
          // Aquí podrías implementar lógica adicional si es necesario
        }
      }
    };

    // Iniciar seguimiento de tiempo
    const startTracking = () => {
      // Registrar visita inicial
      trackVisit(Date.now());

      // Configurar intervalo para seguimiento de tiempo
      intervalRef.current = setInterval(() => {
        if (isMounted) {
          const currentTime = Date.now();
          const calculatedElapsedTime = Math.floor((currentTime - initialTimeRef.current) / 1000);
          
          // Actualizar tiempo transcurrido
          setElapsedTime(calculatedElapsedTime);
          
          // Registrar visitas periódicas cada 30 segundos
          if (calculatedElapsedTime > 0 && calculatedElapsedTime % 30 === 0) {
            trackVisit(currentTime);
          }
        }
      }, 1000);
    };

    // Iniciar seguimiento
    startTracking();

    // Limpieza al desmontar
    return () => {
      isMounted = false;
      
      // Detener intervalo
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        setIsTracking(false);
      }
      
      // Registrar visita final
      trackVisit(Date.now());
    };
  }, [itemId, user, token, userIP]);

  return {
    elapsedTime,
    isTracking,
    userIP,
    canTrack: !!(itemId && ((user && token) || (areCookiesAccepted() && userIP)))
  };
};